import React, { useCallback, useEffect, useState } from "react";
import {
  AppDatePicker,
  AppFileUploader,
  AppInput,
  AppInputNumber,
  AppModal,
  AppMultiSelect,
  AppRemixIcon,
  AppSingleSelect,
  AppTextArea,
  AppHelpModal,
} from "@/layout/AppComponents";
import axios from "axios";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Formik } from "formik";
import Moment from "moment";
import * as Yup from "yup";
import ReactDOMServer from "react-dom/server";
import { axiosPath } from "../../../../../../../routing/helpers/router";
import * as currentPageAction from "../../../../../../../store/actions/currentPageAction";
import Translator from "../../../../../../components/helpers/Translator";
import { toFormData } from "../../../../../../../minions/forms";
import SelectPlants from "../../../../../../components/form/Selects/SelectPlants";
import NotificationAlert from "../../../../../../components/helpers/notificationAlert";
import ErrorHandler from "../../../../../../components/error/ErrorHandler";
import efficacyValues from "../PenaltyRisk/constants/efficacyValues";
import AppTextEditor from "../../../../../../components/form/Texts/AppTextEditor";

const CorrectiveActionsNewTask = (props) => {
  const { plantObligationID, onCreated, modulePermissions } = props;
  const { moduleBridgeHex, plantHex } = useParams();
  const dispatch = useDispatch();
  const permissions = useSelector(
    (state) => state.module[moduleBridgeHex].permissions,
  );
  const [typeOptions, setTypeOptions] = useState();
  const [responsibleOptions, setResponsibleOptions] = useState();
  const [showMultiObligationModal, setShowMultiObligationModal] =
    useState(false);
  const poLevel3 = useSelector(
    (s) => s.currentPage?.plantObligationLevels?.level3,
  );
  const [penaltyRiskInitialValues] = useState(
    permissions?.gcl?.module?.penaltyRisk?.type === "acciona"
      ? {
          accionaPenaltyRiskCode: "",
          accionaPenaltyRiskNature: null,
          accionaPenaltyRiskAutomation: null,
        }
      : permissions?.gcl?.module?.penaltyRisk?.type === "copisa"
        ? {
            copisaPenaltyRiskImplementationDregree: null,
            copisaPenaltyRiskEfficiency: null,
          }
        : undefined,
  );

  // < GET LISTS
  const getEditLists = async () => {
    try {
      const response = await axios.post(
        axiosPath(
          "api_r_user_module_legalCompliance_correctiveActions_task_getEditLists",
        ),
        {
          moduleBridgeHex,
          plantHex,
          plantObligationID,
        },
      );
      return response.data;
    } catch (err) {}
  };
  useEffect(() => {
    getEditLists().then((r) => {
      setTypeOptions(r.typeOptions);
      setResponsibleOptions(r.responsibleOptions);
    });
  }, []);
  // > GET LISTS

  // < NEW TASK
  const accionaPenaltyRiskUpdate = useCallback(
    async () =>
      axios
        .post(
          axiosPath(
            "api_r_user_module_legalCompliance_accionaPenaltyRisk_update",
          ),
          {
            moduleBridgeHex,
            plantHex,
            plantObligationID,
          },
        )
        .then((r) => {
          const updatedLevel3 = { ...poLevel3 };
          updatedLevel3[plantObligationID].accionaPenaltyRisk = {
            residualImpact: r.data?.vulnerability?.residualImpact,
            residualProbability: r.data?.vulnerability?.residualProbability,
          };
          dispatch(
            currentPageAction.change("plantObligationLevels", {
              level3: updatedLevel3,
            }),
          );
        }),
    [moduleBridgeHex, plantHex, plantObligationID, poLevel3],
  );

  const copisaPenaltyRiskUpdate = useCallback(
    async () =>
      axios
        .post(
          axiosPath(
            "api_r_user_module_legalCompliance_copisaPenaltyRisk_update",
          ),
          {
            moduleBridgeHex,
            plantHex,
            plantObligationID,
          },
        )
        .then((r) => {
          const updatedLevel3 = { ...poLevel3 };
          updatedLevel3[plantObligationID].copisaPenaltyRisk = {
            residualImpact: r.data?.impact?.efficacyAverage,
            residualReputation: r.data?.impact?.reputational,
            residualProbability:
              r.data?.inherentProbability?.residualProbability,
          };
          dispatch(
            currentPageAction.change("plantObligationLevels", {
              level3: updatedLevel3,
            }),
          );
        }),
    [moduleBridgeHex, plantHex, plantObligationID, poLevel3],
  );

  const handleNewTask = async (values) =>
    axios
      .post(
        axiosPath(
          "api_r_user_module_legalCompliance_correctiveActions_task_new",
        ),
        toFormData({
          ...values,
          ...{
            moduleBridgeHex,
            plantHex,
            plantObligationID,
          },
        }),
        { headers: { "Content-Type": "multipart/form-data" } },
      )
      .then((r) => {
        dispatch(
          currentPageAction.change(
            "correctiveActionsTabs",
            { [plantObligationID]: r.data },
            true,
          ),
        );

        switch (permissions?.gcl?.module?.penaltyRisk?.type) {
          case "acciona":
            accionaPenaltyRiskUpdate();
            break;
          case "copisa":
            copisaPenaltyRiskUpdate();
            break;
          default:
            break;
        }

        onCreated();
        if (r.status === 202) {
          NotificationAlert({
            title: (
              <Translator
                file="module-correctiveActions"
                name="module.ca.info"
              />
            ),
            message:
              "No todas las vinculaciones han podido realizarse. Comprueba la lista de vinculaciones de la tarea para más información.",
            type: "info",
            duration: 5000,
          });
        }
      })
      .catch((e) => dispatch(ErrorHandler(e)));
  // > NEW TASK

  const renderPenaltyRisk = useCallback(
    (values, setFieldValue) => {
      switch (permissions?.gcl?.module?.penaltyRisk?.type) {
        case "acciona":
          return (
            <div className="row">
              <div className="col-lg-4">
                <AppInput
                  title="Código"
                  value={values?.accionaPenaltyRiskCode}
                  icon={<AppRemixIcon icon="calendar" />}
                  onChange={(v) => setFieldValue("accionaPenaltyRiskCode", v)}
                />
              </div>
              <div className="col-lg-4">
                <AppSingleSelect
                  title="Clasificación del control"
                  value={values?.accionaPenaltyRiskNature}
                  icon={<AppRemixIcon icon="apps-2" />}
                  onChange={(v) => setFieldValue("accionaPenaltyRiskNature", v)}
                  options={[
                    {
                      label: "Detectivo",
                      value: 1,
                    },
                    {
                      label: "Preventivo",
                      value: 3,
                    },
                  ]}
                />
              </div>
              <div className="col-lg-4">
                <AppSingleSelect
                  title="Tipo de control"
                  value={values?.accionaPenaltyRiskAutomation}
                  icon={<AppRemixIcon icon="apps-2" />}
                  onChange={(v) =>
                    setFieldValue("accionaPenaltyRiskAutomation", v)
                  }
                  options={[
                    {
                      label: "Manual",
                      value: 1,
                    },
                    {
                      label: "Semiautomático",
                      value: 2,
                    },
                    {
                      label: "Automático",
                      value: 3,
                    },
                  ]}
                />
              </div>
            </div>
          );
        case "copisa":
          const implementationDregreeOptions = [
            {
              label: "Implantado < 25 %",
              value: 0,
            },
            {
              label: "Implantado en entre 25 % y 50 %",
              value: 0.25,
            },
            {
              label: "Implantado en entre 50 % y 75 %",
              value: 0.5,
            },
            {
              label: "Implantado en entre 75 % y 95 %",
              value: 0.75,
            },
            {
              label: "Implantado en > 95 %",
              value: 1,
            },
          ];
          const efficiencyOptions = [
            {
              label: "0 puntos",
              value: 0,
            },
            {
              label: "1 punto",
              value: 0.2,
            },
            {
              label: "2 puntos",
              value: 0.4,
            },
            {
              label: "3 puntos",
              value: 0.6,
            },
            {
              label: "4 puntos",
              value: 0.8,
            },
            {
              label: "5 – 6 puntos",
              value: 1,
            },
          ];

          return (
            <div className="row">
              <div className="col-lg-4">
                <AppSingleSelect
                  title="Grado de implentación"
                  value={values?.copisaPenaltyRiskImplementationDregree}
                  icon={<AppRemixIcon icon="apps-2" />}
                  onChange={(v) =>
                    setFieldValue("copisaPenaltyRiskImplementationDregree", v)
                  }
                  options={implementationDregreeOptions}
                />
              </div>
              <div className="col-lg-4 d-flex align-items-center">
                <AppSingleSelect
                  title="Eficacia"
                  value={values?.copisaPenaltyRiskEfficiency}
                  icon={<AppRemixIcon icon="apps-2" />}
                  onChange={(v) =>
                    setFieldValue("copisaPenaltyRiskEfficiency", v)
                  }
                  options={efficiencyOptions}
                  containerClassName="flex-grow-1"
                />
                <div className="ml-1">
                  <AppHelpModal title="Ayuda" size="lg">
                    <div className="table-responsive">
                      <table className="table table-hover table-sm">
                        <thead>
                          <tr>
                            <th>Valor 0</th>
                            <th>Valor 1</th>
                          </tr>
                        </thead>
                        <tbody>
                          {efficacyValues.map((ev, i) => (
                            <tr key={i}>
                              <td>{ev.value0}</td>
                              <td>{ev.value1}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </AppHelpModal>
                </div>
              </div>
              <div className="col-lg-4">
                <AppInput
                  icon={<AppRemixIcon icon="hashtag" noVariants />}
                  title="Efectividad"
                  isReadOnly
                />
              </div>
            </div>
          );
        default:
          return null;
      }
    },
    [permissions],
  );

  const validationSchema = Yup.object().shape({
    date: Yup.string().required(
      ReactDOMServer.renderToString(<Translator file="module-legalCompliance" name="must.select.date" />),
    ),
    limitDate: Yup.string().required(
      ReactDOMServer.renderToString(<Translator file="module-legalCompliance" name="must.select.date" />),
    ),
    type: Yup.mixed().required(
      ReactDOMServer.renderToString(<Translator file="module-legalCompliance" name="must.select.origin" />),
    ),
    comment: Yup.string().required(
      ReactDOMServer.renderToString(<Translator file="user" name="user.required" />),
    ),
  });

  return (
    <>
      <Formik
        validationSchema={validationSchema}
        validateOnChange={false}
        validateOnBlur={false}
        enableReinitialize
        initialValues={{
          date: Moment().format("DD/MM/YYYY"),
          limitDate: Moment().add(1, "month").format("DD/MM/YYYY"),
          type: null,
          initialProbability: 0,
          weight: "0.00",
          comment: "",
          employees: [],
          externalEmployees: "",
          file: null,
          multiObligationHexs: "",
          multiObligationPlantHexs: [],
          ...penaltyRiskInitialValues,
        }}
        onSubmit={handleNewTask}
      >
        {({ setFieldValue, handleSubmit, values, errors, onSubmitting }) => (
          <>
            <div className="row-no-margin">
              <div className="row">
                <div className="col-lg-12">
                  <h3>
                    <Translator
                      file="module-correctiveActions"
                      name="module.ca.new-task"
                    />
                  </h3>
                </div>
              </div>
              <div className="row">
                <div className="col-lg-4">
                  <AppDatePicker
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-legalCompliance"
                        name="creation.date"
                      />,
                    )}
                    value={values.date}
                    onChange={(opt) => setFieldValue("date", opt)}
                    placeholder="dd/mm/yyyy"
                    error={errors.date}
                  />
                </div>
                <div className="col-lg-4">
                  <AppDatePicker
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-legalCompliance"
                        name="module.lc.limit.date"
                      />,
                    )}
                    value={values.limitDate}
                    onChange={(opt) => setFieldValue("limitDate", opt)}
                    placeholder="dd/mm/yyyy"
                    error={errors.limitDate}
                  />
                </div>
                <div className="col-lg-4">
                  <AppSingleSelect
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-correctiveActions"
                        name="module.ca.gcl-task-type"
                      />,
                    )}
                    icon={<AppRemixIcon icon="focus" />}
                    value={values.type}
                    options={typeOptions}
                    onChange={(v) => setFieldValue("type", v)}
                    isLoading={!typeOptions}
                    error={errors.type}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-lg-4">
                  <AppFileUploader
                    placeholder={
                      !values.file
                        ? ReactDOMServer.renderToString(
                            <Translator file="general" name="select.a.file" />,
                          )
                        : values.file[0].name
                    }
                    onChange={(opt) => setFieldValue("file", opt)}
                    btnText={<Translator file="general" name="search..." />}
                  />
                </div>
                {!permissions.gcl?.module?.submodules?.correctiveActions
                  ?.supervisionSystem &&
                  !modulePermissions?.removeWorldlexEmployee && (
                    <div className="col-lg-4">
                      <AppMultiSelect
                        title={ReactDOMServer.renderToString(
                          <Translator
                            file="module-legalCompliance"
                            name="responsibles"
                          />,
                        )}
                        icon={<AppRemixIcon icon="user" />}
                        value={values.employees}
                        options={responsibleOptions}
                        onChange={(v) => setFieldValue("employees", v)}
                        isLoading={!responsibleOptions}
                        hasSearchBox
                        hasSelectOptions
                      />
                    </div>
                  )}
                {!permissions.gcl?.module?.submodules?.correctiveActions
                  ?.supervisionSystem && (
                  <div className="col-lg-4">
                    <AppInput
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-correctiveActions"
                          name="module.ca.external-responsibles"
                        />,
                      )}
                      icon={<AppRemixIcon icon="user" />}
                      value={values.externalEmployees}
                      onChange={(v) => setFieldValue("externalEmployees", v)}
                      placeholder={ReactDOMServer.renderToString(
                        <Translator
                          file="module-correctiveActions"
                          name="module.ca.external-responsibles.placeholder"
                        />,
                      )}
                    />
                  </div>
                )}
              </div>
              {modulePermissions?.caAutoCalc &&
                modulePermissions.caAutoCalc == 1 && (
                  <div className="row">
                    <div className="col-lg-6">
                      <AppSingleSelect
                        title={ReactDOMServer.renderToString(
                          <Translator
                            file="module-correctiveActions"
                            name="initial.probability"
                          />,
                        )}
                        value={values.initialProbability}
                        icon={<AppRemixIcon icon="calendar" />}
                        onChange={(v) => setFieldValue("initialProbability", v)}
                        options={[
                          {
                            label: ReactDOMServer.renderToString(
                              <Translator
                                file="filter"
                                name="filter.probability.0"
                              />,
                            ),
                            value: 0,
                          },
                          {
                            label: ReactDOMServer.renderToString(
                              <Translator
                                file="filter"
                                name="filter.probability.1"
                              />,
                            ),
                            value: 1,
                          },
                          {
                            label: ReactDOMServer.renderToString(
                              <Translator
                                file="filter"
                                name="filter.probability.2"
                              />,
                            ),
                            value: 2,
                          },
                          {
                            label: ReactDOMServer.renderToString(
                              <Translator
                                file="filter"
                                name="filter.probability.3"
                              />,
                            ),
                            value: 3,
                          },
                        ]}
                      />
                    </div>
                    <div className="col-lg-6">
                      <AppInputNumber
                        title={ReactDOMServer.renderToString(
                          <Translator
                            file="module-correctiveActions"
                            name="weight"
                          />,
                        )}
                        value={values.weight}
                        icon={<AppRemixIcon icon="calendar" />}
                        onChange={(v) => setFieldValue("weight", v)}
                        decimals={2}
                      />
                    </div>
                  </div>
                )}
              {renderPenaltyRisk(values, setFieldValue)}
              <div className="row">
                <div className="col-lg-12">
                  <AppTextArea
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-legalCompliance"
                        name="comment"
                      />,
                    )}
                    height={
                      modulePermissions?.caAutoCalc &&
                      modulePermissions.caAutoCalc == 1
                        ? "140px"
                        : "190px"
                    }
                    isReadOnly
                    value={
                      <AppTextEditor
                        value={values.comment}
                        onChange={(opt) => setFieldValue("comment", opt)}
                        toolbar={{
                          toolbar: [["bold", "italic"]],
                        }}
                        height={117}
                      />
                    }
                    error={errors.comment}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-lg-12">
                  <div className="float-right">
                    {permissions.gcl?.module?.submodules?.correctiveActions
                      ?.taskMultiObligation && (
                      <>
                        <AppModal
                          show={showMultiObligationModal}
                          onClose={() => setShowMultiObligationModal(false)}
                        >
                          <p>
                            Se aplicará a las obligaciones de todas las
                            instalaciones seleccionadas, además de a esta.
                            <br />
                            Si los códigos introducidos no se corresponden con
                            obligaciones de las instalaciones seleccionadas, no
                            se creará la vinculación.
                          </p>
                          <SelectPlants
                            type="module"
                            format="p"
                            onlyUsers
                            useHex
                            value={values.multiObligationPlantHexs}
                            onHandleChange={(opt) => {
                              setFieldValue("multiObligationPlantHexs", opt);
                            }}
                            elements={[moduleBridgeHex]}
                            isMulti
                          />
                          <AppInput
                            title="CODE de Obligaciones"
                            icon={<AppRemixIcon icon="user" />}
                            value={values.multiObligationHexs}
                            onChange={(v) =>
                              setFieldValue(
                                "multiObligationHexs",
                                v.replace(/[^0-9a-fA-F,]/, ""),
                              )
                            }
                            placeholder="Introducir CODEs separados por comas. Sin espacios."
                          />
                          <button
                            type="submit"
                            disabled={onSubmitting}
                            className="btn btn-outline-info"
                            onClick={handleSubmit}
                          >
                            <Translator
                              file="module-legalCompliance"
                              name="create.task"
                            />
                          </button>
                        </AppModal>
                        <span
                          className="btn btn-outline-primary mr-2"
                          onClick={() => setShowMultiObligationModal(true)}
                        >
                          Crear y vincular a más obligaciones
                        </span>
                      </>
                    )}

                    <span
                      className="btn btn-outline-light mr-2"
                      onClick={() => onCreated()}
                    >
                      <Translator file="user" name="user.cancel" />
                    </span>
                    <button
                      type="submit"
                      disabled={onSubmitting}
                      className="btn btn-outline-info"
                      onClick={handleSubmit}
                    >
                      <Translator
                        file="module-legalCompliance"
                        name="create.task"
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </Formik>
    </>
  );
};
export default CorrectiveActionsNewTask;
